import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Server, Search, X } from "lucide-react";

const SERIES_ORDER = ["Entry", "Mid-range", "High-end", "Datacenter", "Hyperscale", "Rugged", "WiFi"];
const SERIES_COLORS = {
  Entry:      "bg-blue-50 text-blue-700 border-blue-200",
  "Mid-range":"bg-green-50 text-green-700 border-green-200",
  "High-end": "bg-amber-50 text-amber-700 border-amber-200",
  Datacenter: "bg-purple-50 text-purple-700 border-purple-200",
  Hyperscale: "bg-red-50 text-red-700 border-red-200",
  Rugged:     "bg-zinc-100 text-zinc-700 border-zinc-300",
  WiFi:       "bg-indigo-50 text-indigo-700 border-indigo-200",
};

function fmtPrice(n) {
  if (n == null || n === 0) return "—";
  return n >= 10000
    ? n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })
    : n.toLocaleString("fr-FR", { maximumFractionDigits: 0 });
}

export default function ManualModelsPanel({ value, onChange }) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeSeries, setActiveSeries] = useState("Entry");

  useEffect(() => {
    api.get("/fortigate/models")
      .then((r) => setModels(r.data || []))
      .catch(() => setModels([]))
      .finally(() => setLoading(false));
  }, []);

  const items = value || [];
  const updateRow = (idx, patch) => {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addRow = (sku) => {
    if (items.some((it) => it.model === sku)) return;
    onChange([...items, { model: sku, quantity: 1, role: "Site" }]);
  };
  const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));

  const seriesCounts = useMemo(() => {
    const c = {};
    for (const m of models) c[m.series] = (c[m.series] || 0) + 1;
    return c;
  }, [models]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return models.filter((m) => {
      if (items.some((it) => it.model === m.sku)) return false;
      if (q) {
        return (
          m.sku.toLowerCase().includes(q) ||
          (m.name || "").toLowerCase().includes(q)
        );
      }
      return m.series === activeSeries;
    });
  }, [models, items, search, activeSeries]);

  const selectedTotal = items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);

  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid="manual-models-panel">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-100 flex items-start gap-3">
        <Server className="h-4 w-4 text-[#D42B1E] mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-zinc-900">Sélection manuelle des FortiGate</p>
          <p className="text-xs text-zinc-500 mt-0.5">
            {models.length || "…"} modèles disponibles dans la price list officielle.
            Le moteur attache les bundles + extensions Année 2 selon la durée et le niveau de sécurité.
          </p>
        </div>
        {items.length > 0 && (
          <span className="px-2 py-1 bg-[#D42B1E] text-white text-xs font-mono rounded-sm shrink-0">
            {items.length} modèle{items.length > 1 ? "s" : ""} · {selectedTotal} unité{selectedTotal > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Selected list */}
      {items.length > 0 && (
        <div className="px-5 py-3 border-b border-zinc-100 bg-red-50/30 space-y-2" data-testid="manual-models-selected">
          {items.map((it, idx) => {
            const meta = models.find((m) => m.sku === it.model);
            return (
              <div key={it.model} className="flex items-center gap-3 bg-white border border-zinc-200 rounded-sm px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-zinc-900">{it.model}</span>
                    {meta?.series && (
                      <span className={`px-1.5 py-0.5 text-[10px] rounded-sm border font-medium ${SERIES_COLORS[meta.series]}`}>
                        {meta.series}
                      </span>
                    )}
                    {meta?.price > 0 && (
                      <span className="text-[11px] font-mono text-zinc-500">${fmtPrice(meta.price)}</span>
                    )}
                  </div>
                  {meta?.name && (
                    <p className="text-[11px] text-zinc-500 truncate">{meta.name}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Input
                    value={it.role || ""}
                    onChange={(e) => updateRow(idx, { role: e.target.value })}
                    className="w-24 rounded-sm h-8 text-xs"
                    placeholder="HQ, Agence..."
                    data-testid={`manual-role-input-${it.model}`}
                  />
                  <Input
                    type="number"
                    min={1}
                    max={9999}
                    value={it.quantity}
                    onChange={(e) => updateRow(idx, { quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                    className="w-14 rounded-sm h-8 text-xs text-center"
                    data-testid={`manual-qty-input-${it.model}`}
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(idx)} className="h-8 w-8 p-0" data-testid={`manual-remove-${it.model}`}>
                    <Trash2 className="h-3.5 w-3.5 text-zinc-500" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Search + series tabs */}
      <div className="px-5 py-3 border-b border-zinc-100 bg-zinc-50/40">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
          <Input
            placeholder="Rechercher un modèle (ex. FG-200, 1000F, Rugged…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9 rounded-sm h-9 text-sm"
            data-testid="manual-search-input"
          />
          {search && (
            <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2">
              <X className="h-3.5 w-3.5 text-zinc-400 hover:text-zinc-700" />
            </button>
          )}
        </div>
        {!search && (
          <div className="mt-3 flex flex-wrap gap-1" data-testid="manual-series-tabs">
            {SERIES_ORDER.filter((s) => seriesCounts[s]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setActiveSeries(s)}
                className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${
                  activeSeries === s
                    ? `${SERIES_COLORS[s]} font-semibold`
                    : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"
                }`}
                data-testid={`manual-series-tab-${s}`}
              >
                {s} <span className="ml-1 opacity-60">({seriesCounts[s]})</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Models grid (compact list) */}
      <div className="px-5 py-3">
        {loading ? (
          <p className="text-sm text-zinc-500 py-6 text-center">Chargement de la price list…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center italic">
            {search ? "Aucun modèle ne correspond à votre recherche." : "Tous les modèles de cette série ont été ajoutés."}
          </p>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold mb-2">
              {search ? `${filtered.length} résultat(s)` : `${filtered.length} modèle(s) ${activeSeries}`}
            </p>
            <div className="max-h-[320px] overflow-y-auto -mx-1" data-testid="manual-models-catalog">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 px-1">
                {filtered.slice(0, 200).map((m) => (
                  <button
                    key={m.sku}
                    type="button"
                    onClick={() => addRow(m.sku)}
                    className="flex items-center justify-between gap-2 border border-zinc-200 rounded-sm px-3 py-2 text-left hover:border-[#D42B1E] hover:bg-red-50/30 transition-colors group"
                    data-testid={`manual-add-${m.sku}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs text-zinc-900">{m.sku}</span>
                        {m.in_catalog && (
                          <span className="text-[9px] bg-green-100 text-green-700 px-1 rounded-sm font-medium">Optimisé</span>
                        )}
                      </div>
                      <p className="text-[10px] text-zinc-500 truncate mt-0.5">
                        {m.price > 0 ? `$${fmtPrice(m.price)}` : "Prix sur demande"}
                        {m.max_users ? ` · ≤ ${m.max_users} util.` : ""}
                      </p>
                    </div>
                    <Plus className="h-3.5 w-3.5 text-zinc-400 group-hover:text-[#D42B1E] shrink-0" />
                  </button>
                ))}
              </div>
              {filtered.length > 200 && (
                <p className="px-1 mt-2 text-[11px] text-zinc-500 italic text-center">
                  + {filtered.length - 200} autres résultats — affinez votre recherche.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
